/**
 * Class Representative (CR) → class mapping helpers.
 *
 * CR role names follow the convention `cr_<program><class_number>`, e.g.
 * `cr_pst4` represents the CR for class `PST4`. These helpers derive the
 * allowed classes from the role string itself so we never get out of sync.
 */

export const CR_ROLE_NAMES = [
  "cr_cmt4", "cr_cmt5", "cr_cmt6",
  "cr_pst4", "cr_pst5", "cr_pst6",
  "cr_sw4",  "cr_sw5",  "cr_sw6",
] as const;

/** Roles that override the CR class restriction (can scan any class). */
const CR_BYPASS_ROLES = new Set([
  "superadmin",
  "head_academic",
  "head_administration",
  "it_incharge",
  "security_man",
  "principal",
  "deputy_principal",
]);

/** Convert a CR role like `cr_pst4` to the class label `PST4`. */
export function crRoleToClass(role: string): string | null {
  if (!role || !role.startsWith("cr_")) return null;
  const suffix = role.slice(3); // "pst4"
  if (!suffix) return null;
  return suffix.toUpperCase();
}

/** Returns the unique uppercased class labels a user is a CR for. */
export function getCrAllowedClasses(roles: string[]): string[] {
  const out = new Set<string>();
  for (const r of roles ?? []) {
    const cls = crRoleToClass(r);
    if (cls) out.add(cls);
  }
  return Array.from(out);
}

/**
 * True when the user has at least one CR role and NO bypass role.
 * In that case we must restrict their session selection to their own class.
 */
export function isCrOnly(roles: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  const hasCr = roles.some((r) => r.startsWith("cr_"));
  if (!hasCr) return false;
  return !roles.some((r) => CR_BYPASS_ROLES.has(r));
}

/** Normalise a class label for comparison (uppercased, trimmed). */
export function normaliseClass(cls: string | null | undefined): string {
  return (cls ?? "").trim().toUpperCase();
}
